import React, { useEffect, useRef, useState } from 'react';
import { Button, Group, ScrollArea, Tabs } from '@mantine/core';
import { IOrgJob, ISourceMatch, ISubmitMatchesResponse } from '../PropagateMatchesTypes';
import { Link, useHistory } from 'react-router-dom';
import Job from './Job';
import Loading from './Loading';
import Matches from './Matches';

type Props = {
  activeTab: string;
  fetchSurveyJobMatchData: (params: {
    resetSelectedMatches?: boolean;
    surveyJobMatch?: ISourceMatch;
    matchesMade?: ISubmitMatchesResponse;
  }) => void;
  isLoading: boolean;
  orgJobs: IOrgJob[];
  setActiveTab: (activeTab: string) => void;
  setSurveyJobMatches: React.Dispatch<React.SetStateAction<ISourceMatch[]>>;
  surveyJobMatches: ISourceMatch[];
  toggledTargetMatches: string[];
  updateToggledMatches: (id: string) => void;
};

const TabsList: React.FC<Props> = ({
  activeTab,
  fetchSurveyJobMatchData,
  isLoading,
  orgJobs,
  setActiveTab,
  setSurveyJobMatches,
  surveyJobMatches,
  toggledTargetMatches,
  updateToggledMatches,
}) => {
  const [showArrows, setShowArrows] = useState(false);
  const history = useHistory();
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const jobIDFromHistory: number | null = history.location?.state?.jobID;

  const checkOverflow = () => {
    if (tabsRef.current) {
      const isOverflowing = tabsRef.current.scrollWidth > tabsRef.current.clientWidth;
      setShowArrows(isOverflowing);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  const scrollLeft = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollLeft -= 200;
    }
  };

  const scrollRight = () => {
    if (tabsRef.current) {
      tabsRef.current.scrollLeft += 200;
    }
  };

  return (
    <Tabs mt="20" onChange={(value) => setActiveTab(value || '')} value={activeTab}>
      <div className="tabs-container">
        {showArrows && (
          <i className="arrow-button icon-left-open" onClick={scrollLeft} />
        )}
        <div className="tabs-wrapper">
          <Tabs.List ref={tabsRef} className="tabs-scrollarea">
            {orgJobs.map(({ jobTitle, orgJobId }) => (
              <Tabs.Tab className="propagate__tab" key={`tab-${orgJobId}`} value={`${orgJobId}`}>
                {jobTitle || 'Not Set'}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </div>
        {showArrows && (
          <i className="arrow-button icon-right-open" onClick={scrollRight} />
        )}
        <Button
          className="submit-button"
          component={Link}
          fz="sm"
          m="0 20"
          size="xl"
          to={{
            pathname: jobIDFromHistory ? `/market-analysis/jobs/${jobIDFromHistory}/pricing` : '/market-analysis/market-pricing',
            state: {
              notFirstLoad: true,
              searchQuery: (location as any)?.state?.searchQuery,
              offset: (location as any)?.state?.offset,
              bulkPricing: true,
            },
          }}
        >
          Close
        </Button>
      </div>
      {orgJobs.map((orgJob: IOrgJob) => (
        <Tabs.Panel key={`panel-${orgJob.orgJobId}`} value={`${orgJob.orgJobId}`}>
          <Job fetchSurveyJobMatchData={fetchSurveyJobMatchData} orgJob={orgJob} surveyJobMatches={surveyJobMatches} />
          <ScrollArea className="propagate-matches__panel" key={orgJob.orgJobId}>
            {isLoading ? (
              <Loading />
            ) : (
              surveyJobMatches.map((surveyJobMatch: ISourceMatch) => (
                <Matches
                  activeTab={activeTab}
                  fetchSurveyJobMatchData={fetchSurveyJobMatchData}
                  key={surveyJobMatch.sourceMatchId}
                  orgJobs={orgJobs}
                  setSurveyJobMatches={setSurveyJobMatches}
                  surveyJobMatch={surveyJobMatch}
                  surveyJobMatches={surveyJobMatches}
                  toggledTargetMatches={toggledTargetMatches}
                  updateToggledMatches={updateToggledMatches}
                />
              ))
            )}
          </ScrollArea>
        </Tabs.Panel>
      ))}
    </Tabs>
  );
};

export default TabsList;
